import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Sliders, Save, X, Star } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmt = (c) => `A$${(c / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const centsToShort = (c) => {
  const v = c / 100;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return `${Math.round(v)}`;
};

export default function NetWorthProjection() {
  const [data, setData] = useState(null);
  const [invest, setInvest] = useState(6);
  const [superPct, setSuperPct] = useState(8);
  const [savingsOverride, setSavingsOverride] = useState("");
  const [months, setMonths] = useState(12);
  const [loading, setLoading] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [savingName, setSavingName] = useState("");

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const body = { investment_return_pct: invest, super_growth_pct: superPct, months };
      if (savingsOverride) body.monthly_savings_cents = Math.round(parseFloat(savingsOverride) * 100);
      const r = await api.post("/money/projection", body);
      setData(r.data);
    } finally { setLoading(false); }
  }, [invest, superPct, months, savingsOverride]);

  const loadScenarios = useCallback(async () => {
    const r = await api.get("/money/projection/scenarios");
    setScenarios(r.data);
    const def = r.data.find((s) => s.is_default);
    if (def && !activeScenarioId) {
      setInvest(def.assumptions.investment_return_pct);
      setSuperPct(def.assumptions.super_growth_pct);
      setMonths(def.assumptions.months);
      setSavingsOverride(def.assumptions.monthly_savings_cents ? (def.assumptions.monthly_savings_cents / 100).toString() : "");
      setActiveScenarioId(def.id);
    }
  }, [activeScenarioId]);
  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  useEffect(() => { run(); }, [run]);

  const applyScenario = (s) => {
    setInvest(s.assumptions.investment_return_pct);
    setSuperPct(s.assumptions.super_growth_pct);
    setMonths(s.assumptions.months);
    setSavingsOverride(s.assumptions.monthly_savings_cents ? (s.assumptions.monthly_savings_cents / 100).toString() : "");
    setActiveScenarioId(s.id);
  };
  const saveScenario = async () => {
    if (!savingName.trim()) return;
    const body = {
      name: savingName.trim(),
      assumptions: {
        investment_return_pct: invest, super_growth_pct: superPct, months,
        monthly_savings_cents: savingsOverride ? Math.round(parseFloat(savingsOverride) * 100) : null,
      },
      is_default: scenarios.length === 0,
    };
    const r = await api.post("/money/projection/scenarios", body);
    toast.success(`Scenario "${r.data.name}" saved`);
    setSavingName("");
    loadScenarios();
  };
  const deleteScenario = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/money/projection/scenarios/${id}`);
    if (activeScenarioId === id) setActiveScenarioId(null);
    loadScenarios();
  };
  const setDefault = async (id, e) => {
    e.stopPropagation();
    await api.patch(`/money/projection/scenarios/${id}`, { is_default: true });
    toast.success("Set as default");
    loadScenarios();
  };

  if (loading && !data) return <div className="col-span-full rounded-hero bg-surface-container p-6 text-sm text-surface-onVariant">Loading projection…</div>;
  if (!data) return null;

  const finalCents = data.projection[data.projection.length - 1]?.projected_cents ?? data.current_net_cents;
  const change = finalCents - data.current_net_cents;

  const chartData = [
    { month_label: "Now", projected_cents: data.current_net_cents },
    ...data.projection,
  ];

  return (
    <div className="col-span-full rounded-hero bg-surface-container p-6" data-testid="net-worth-projection">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-medium text-surface-on">Net worth projection</h3>
          <p className="text-xs text-surface-onVariant mt-0.5">{months} months forward · assumes {invest}% annual return + A${(data.monthly_savings_cents / 100).toLocaleString()} monthly savings</p>
        </div>
        <button
          data-testid="projection-toggle-controls"
          onClick={() => setShowControls((v) => !v)}
          className="inline-flex items-center gap-2 rounded-full bg-surface hover:bg-primary/8 text-surface-on text-xs font-medium px-3 py-1.5 transition-colors"
        >
          <Sliders className="w-3.5 h-3.5" /> {showControls ? "Hide" : "Adjust"} assumptions
        </button>
      </div>

      {/* Scenario pills */}
      {scenarios.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="scenario-pills">
          {scenarios.map((s) => (
            <button key={s.id} data-testid={`scenario-${s.id}`} onClick={() => applyScenario(s)}
              className={cn("group inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                activeScenarioId === s.id ? "bg-primary text-primary-foreground" : "bg-surface hover:bg-primary/8 text-surface-on")}>
              {s.is_default && <Star className="w-3 h-3 fill-current" />}
              {s.name}
              <span onClick={(e) => setDefault(s.id, e)} className="opacity-0 group-hover:opacity-100 hover:text-yellow-400 transition-opacity" title="Set default"><Star className="w-3 h-3" /></span>
              <span onClick={(e) => deleteScenario(s.id, e)} className="opacity-0 group-hover:opacity-100 hover:text-danger transition-opacity" title="Delete"><X className="w-3 h-3" /></span>
            </button>
          ))}
        </div>
      )}

      {showControls && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 bg-surface rounded-2xl p-4" data-testid="projection-controls">
          <Slider label="Investment return %" min={-5} max={15} step={0.5} value={invest} onChange={setInvest} />
          <Slider label="Super growth %" min={-5} max={15} step={0.5} value={superPct} onChange={setSuperPct} />
          <div>
            <div className="text-[10px] font-medium tracking-widest uppercase text-surface-onVariant mb-1">Horizon (months)</div>
            <select value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10))} className="w-full bg-surface-containerHigh rounded-lg px-3 py-2 text-sm text-surface-on outline-none border-b-2 border-outline focus:border-primary">
              {[6, 12, 24, 36, 60].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] font-medium tracking-widest uppercase text-surface-onVariant mb-1">Override savings A$/mo</div>
            <input type="number" data-testid="projection-savings-override" value={savingsOverride} onChange={(e) => setSavingsOverride(e.target.value)}
              placeholder={(data.monthly_savings_cents / 100).toFixed(0)}
              className="w-full bg-surface-containerHigh rounded-lg px-3 py-2 text-sm text-surface-on outline-none border-b-2 border-outline focus:border-primary" />
          </div>
          <div className="col-span-2 md:col-span-4 flex items-center gap-2 pt-2 border-t border-outline-variant">
            <input value={savingName} onChange={(e) => setSavingName(e.target.value)} placeholder='Save as scenario ("Base case", "Career jump"…)'
              className="flex-1 bg-transparent outline-none text-sm text-surface-on py-2 border-b-2 border-outline focus:border-primary" data-testid="scenario-name" />
            <button onClick={saveScenario} disabled={!savingName.trim()} data-testid="scenario-save"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-xs font-medium px-3 py-1.5 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 transition-[transform,filter] duration-200 ease-md">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Tile label="Today" value={fmt(data.current_net_cents)} />
        <Tile label={`In ${months} mo`} value={fmt(finalCents)} tone="primary" />
        <Tile label="Change" value={`${change >= 0 ? "+" : ""}${fmt(change)}`} tone={change >= 0 ? "positive" : "danger"} />
      </div>

      <div className="mt-5">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#CAC4D0" vertical={false} />
            <XAxis dataKey="month_label" stroke="#49454F" style={{ fontSize: 11 }} />
            <YAxis stroke="#49454F" style={{ fontSize: 11 }} tickFormatter={(v) => centsToShort(v)} />
            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ backgroundColor: "#FFFBFE", border: "1px solid #CAC4D0", borderRadius: 12 }} />
            <ReferenceLine y={data.current_net_cents} stroke="#79747E" strokeDasharray="4 4" />
            <Line type="monotone" dataKey="projected_cents" stroke="#6750A4" strokeWidth={2.5} dot={{ r: 3, fill: "#6750A4" }} activeDot={{ r: 5 }} name="Net worth" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <CashForecastPanel fmt={fmt} />
    </div>
  );
}

function CashForecastPanel({ fmt }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/money/cash-forecast?days=60").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const low = d.low_point;
  const daysToLow = Math.round((new Date(low.date) - new Date()) / 86400000);
  return (
    <div className="mt-6 pt-6 border-t border-outline-variant" data-testid="cash-forecast">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h4 className="text-base font-medium text-surface-on">Cash position · next {d.days} days</h4>
          <p className="text-xs text-surface-onVariant mt-0.5">Overlays recurring bills and expected settlement deposits on your live balance</p>
        </div>
        <div className={cn("rounded-full text-xs font-medium px-3 py-1", low.balance_cents < d.start_balance_cents * 0.3 ? "bg-danger-container text-danger" : "bg-primary-container text-primary-onContainer")}>
          Low point: {fmt(low.balance_cents)} in {daysToLow}d
        </div>
      </div>
      <div className="mt-4">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={d.daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="#CAC4D0" vertical={false} />
            <XAxis dataKey="date" stroke="#49454F" style={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} interval={6} />
            <YAxis stroke="#49454F" style={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 100000)}k`} />
            <Tooltip formatter={(v) => fmt(v)} labelFormatter={(l) => l} contentStyle={{ backgroundColor: "#FFFBFE", border: "1px solid #CAC4D0", borderRadius: 12 }} />
            <ReferenceLine x={low.date} stroke="#B3261E" strokeDasharray="4 4" label={{ value: "Low", position: "insideTop", fill: "#B3261E", fontSize: 10 }} />
            <Line type="monotone" dataKey="balance_cents" stroke="#0F5027" strokeWidth={2} dot={false} name="Cash" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Tile({ label, value, tone = "" }) {
  const cls = tone === "primary" ? "text-primary" : tone === "positive" ? "text-[#0F5027]" : tone === "danger" ? "text-danger" : "text-surface-on";
  return (
    <div className="rounded-2xl bg-surface p-4">
      <div className="text-[10px] uppercase tracking-widest text-surface-onVariant">{label}</div>
      <div className={`text-xl font-medium tabular-nums mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange }) {
  return (
    <div>
      <div className="text-[10px] font-medium tracking-widest uppercase text-surface-onVariant mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="flex-1 accent-primary" />
        <span className="text-sm font-medium text-surface-on tabular-nums w-12 text-right">{value}%</span>
      </div>
    </div>
  );
}
